import jsPDF from "jspdf";

// Strict v3 spec: WHITE background, NAVY text + charts, no images, infographic-style.
const NAVY = "#000080";
const NAVY_DEEP = "#0A1455";
const ROYAL = "#2563EB";
const TEXT_DARK = "#0A0E17";
const TEXT_MUTED = "#475569";
const BG_WHITE = "#FFFFFF";
const PANEL_TINT = "#F1F5F9";
const TRACK = "#E2E8F0";

const TIER_COLOR = {
  Critical: "#B91C1C",
  Exposed: "#B45309",
  Performing: "#047857",
  Elite: "#0E7490",
};

const TIER_GUIDANCE = {
  Critical: [
    "Schedule a check-in with a qualified healthcare or wellbeing professional this week.",
    "Pick ONE habit (sleep window, hydration, or daily walk) and protect it for 14 days.",
    "Eliminate the single most disruptive input in your life right now (alcohol, late screens, late food).",
  ],
  Exposed: [
    "Audit your sleep environment and protect a consistent 7+ hour window for 21 days.",
    "Add 3x 30-min sessions of deliberate movement (walking, lifting, mobility) per week.",
    "Replace one ultra-processed meal a day with whole-food protein + vegetables.",
  ],
  Performing: [
    "Layer in active recovery (breathwork, walks, mobility) on training days.",
    "Track one performance metric for 30 days (HRV, RPE, weekly volume).",
    "Schedule one deeper relational or community ritual per week.",
  ],
  Elite: [
    "Mentor someone earlier in their journey — it sharpens your own discipline.",
    "Add micro-experiments (sleep timing, fasting windows, novelty inputs) at the edges.",
    "Audit blind spots: stress, relationships, purpose — not just the metrics that already shine.",
  ],
};

// ---- color helpers ----
const rgb = (hex) => {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};
const fill = (doc, hex) => doc.setFillColor(...rgb(hex));
const stroke = (doc, hex) => doc.setDrawColor(...rgb(hex));
const ink = (doc, hex) => doc.setTextColor(...rgb(hex));

// ---- chart primitives (pure jsPDF — no images) ----
function pageBg(doc) {
  fill(doc, BG_WHITE);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), doc.internal.pageSize.getHeight(), "F");
}

function navyTopRule(doc) {
  fill(doc, NAVY);
  doc.rect(0, 0, 210, 5, "F");
}

function brandBar(doc) {
  // Text-only "FITEST" wordmark (left), report label (right)
  ink(doc, NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setCharSpace(2);
  doc.text("FITEST", 20, 16);
  doc.setCharSpace(0);

  ink(doc, TEXT_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("WELLNESS AUDIT REPORT", 190, 16, { align: "right" });

  stroke(doc, TRACK);
  doc.line(20, 20, 190, 20);
}

function centralFooter(doc, page, totalPages) {
  stroke(doc, TRACK);
  doc.line(20, 275, 190, 275);

  ink(doc, NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(
    "Fitest, a trading name of Leadsopedia Limited (Company: 13145058).",
    105,
    282,
    { align: "center" }
  );
  ink(doc, TEXT_MUTED);
  doc.setFont("helvetica", "normal");
  doc.text("5 Brayford Square, London, E1 0SG.", 105, 287, { align: "center" });

  doc.setFontSize(7);
  doc.text(`Page ${page} of ${totalPages}`, 190, 292, { align: "right" });
}

// Approximate a circle/arc using a polyline of small line segments
function arc(doc, cx, cy, r, startDeg, endDeg, lineWidth, hex) {
  stroke(doc, hex);
  doc.setLineWidth(lineWidth);
  doc.setLineCap("round");
  const seg = Math.max(2, Math.ceil((endDeg - startDeg) / 2)); // 2-degree segments
  let prevX = cx + r * Math.cos((startDeg * Math.PI) / 180);
  let prevY = cy + r * Math.sin((startDeg * Math.PI) / 180);
  for (let i = 1; i <= seg; i++) {
    const t = startDeg + ((endDeg - startDeg) * i) / seg;
    const x = cx + r * Math.cos((t * Math.PI) / 180);
    const y = cy + r * Math.sin((t * Math.PI) / 180);
    doc.line(prevX, prevY, x, y);
    prevX = x;
    prevY = y;
  }
  doc.setLineWidth(0.2);
}

function scoreGauge(doc, cx, cy, r, score, tierColor) {
  // Background ring
  arc(doc, cx, cy, r, 135, 405, 6, TRACK);
  // Score arc (135° start → 135 + 270° max range)
  const pct = Math.max(0, Math.min(100, score)) / 100;
  arc(doc, cx, cy, r, 135, 135 + 270 * pct, 6, tierColor);

  // Tier zone tick marks at 25/50/75 thresholds
  const ticks = [0.25, 0.5, 0.75];
  ticks.forEach((t) => {
    const ang = ((135 + 270 * t) * Math.PI) / 180;
    const x1 = cx + (r - 4) * Math.cos(ang);
    const y1 = cy + (r - 4) * Math.sin(ang);
    const x2 = cx + (r + 4) * Math.cos(ang);
    const y2 = cy + (r + 4) * Math.sin(ang);
    stroke(doc, TEXT_MUTED);
    doc.setLineWidth(0.4);
    doc.line(x1, y1, x2, y2);
  });
  doc.setLineWidth(0.2);
}

function donut(doc, cx, cy, rOuter, rInner, slices) {
  // Approximate filled donut wedges with many thin arcs
  const total = slices.reduce((s, x) => s + x.value, 0);
  if (total <= 0) {
    arc(doc, cx, cy, (rOuter + rInner) / 2, 0, 360, rOuter - rInner, TRACK);
    return;
  }
  let cursor = -90; // start at top
  slices.forEach((s) => {
    const sweep = (s.value / total) * 360;
    if (sweep <= 0) return;
    arc(doc, cx, cy, (rOuter + rInner) / 2, cursor, cursor + sweep, rOuter - rInner, s.color);
    cursor += sweep;
  });
}

function categoryAverages(answers, questions) {
  const map = {};
  questions.forEach((q) => {
    const v = Number(answers[q.id]);
    if (!Number.isFinite(v)) return;
    if (!map[q.category]) map[q.category] = { sum: 0, count: 0 };
    map[q.category].sum += v;
    map[q.category].count += 1;
  });
  return Object.entries(map).map(([cat, { sum, count }]) => ({
    category: cat,
    pct: Math.round((sum / (count * 5)) * 100),
  }));
}

function tierSummary(cats) {
  const buckets = { Critical: 0, Exposed: 0, Performing: 0, Elite: 0 };
  cats.forEach((c) => {
    if (c.pct <= 25) buckets.Critical += 1;
    else if (c.pct <= 50) buckets.Exposed += 1;
    else if (c.pct <= 75) buckets.Performing += 1;
    else buckets.Elite += 1;
  });
  return buckets;
}

// ============================================================================
// Main entry
// ============================================================================
export function generateAuditPdf({ name, score, tier, ownerName, ownerCompany, answers, questions }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const tierColor = TIER_COLOR[tier] || ROYAL;
  const cats = categoryAverages(answers, questions).sort((a, b) => b.pct - a.pct);
  const tBuckets = tierSummary(cats);

  // ============ PAGE 1 — Cover Infographic ============
  pageBg(doc);
  navyTopRule(doc);

  // Wordmark
  ink(doc, NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setCharSpace(4);
  doc.text("FITEST", 20, 36);
  doc.setCharSpace(0);

  ink(doc, TEXT_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Personal Wellness Audit Report", 20, 42);

  // Title
  ink(doc, TEXT_DARK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(30);
  doc.text("Your Wellness", 20, 70);
  doc.text("Audit Result", 20, 84);

  // Recipient strip
  ink(doc, TEXT_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("PREPARED FOR", 20, 98);
  ink(doc, TEXT_DARK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(name || "Respondent", 20, 105);

  if (ownerCompany || ownerName) {
    ink(doc, TEXT_MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("ISSUED BY", 80, 98);
    ink(doc, TEXT_DARK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(ownerCompany || ownerName, 80, 105);
  }

  ink(doc, TEXT_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("DATE", 150, 98);
  ink(doc, TEXT_DARK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(new Date().toLocaleDateString("en-GB"), 150, 105);

  // Score Gauge — centered infographic
  const gx = 70;
  const gy = 165;
  const gr = 40;
  scoreGauge(doc, gx, gy, gr, score, tierColor);

  // Score number at gauge centre
  ink(doc, tierColor);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(48);
  doc.text(String(score), gx, gy + 6, { align: "center" });

  ink(doc, TEXT_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("/ 100", gx, gy + 14, { align: "center" });

  // Tier label below gauge
  ink(doc, tierColor);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setCharSpace(2);
  doc.text(tier.toUpperCase(), gx, gy + 30, { align: "center" });
  doc.setCharSpace(0);

  // Tier legend column on right
  const legend = [
    { label: "Critical", range: "0–25", color: TIER_COLOR.Critical },
    { label: "Exposed", range: "26–50", color: TIER_COLOR.Exposed },
    { label: "Performing", range: "51–75", color: TIER_COLOR.Performing },
    { label: "Elite", range: "76–100", color: TIER_COLOR.Elite },
  ];

  // Legend panel background
  fill(doc, PANEL_TINT);
  doc.roundedRect(125, 130, 65, 70, 3, 3, "F");

  ink(doc, NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setCharSpace(1.5);
  doc.text("TIER SCALE", 132, 140);
  doc.setCharSpace(0);

  legend.forEach((s, i) => {
    const ly = 152 + i * 11;
    fill(doc, s.color);
    doc.roundedRect(132, ly - 3, 4, 4, 1, 1, "F");
    ink(doc, tier === s.label ? TEXT_DARK : TEXT_MUTED);
    doc.setFont("helvetica", tier === s.label ? "bold" : "normal");
    doc.setFontSize(9);
    doc.text(s.label, 140, ly);
    ink(doc, TEXT_MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(s.range, 187, ly, { align: "right" });
  });

  // Category-mix donut on bottom — visualises how many categories sit in each tier
  const donutCx = 60;
  const donutCy = 240;
  donut(doc, donutCx, donutCy, 22, 14, [
    { value: tBuckets.Critical, color: TIER_COLOR.Critical },
    { value: tBuckets.Exposed, color: TIER_COLOR.Exposed },
    { value: tBuckets.Performing, color: TIER_COLOR.Performing },
    { value: tBuckets.Elite, color: TIER_COLOR.Elite },
  ]);

  ink(doc, NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(String(cats.length), donutCx, donutCy + 1, { align: "center" });
  ink(doc, TEXT_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text("CATEGORIES", donutCx, donutCy + 6, { align: "center" });

  // Donut legend
  const donutLegend = [
    { label: "Critical", count: tBuckets.Critical, color: TIER_COLOR.Critical },
    { label: "Exposed", count: tBuckets.Exposed, color: TIER_COLOR.Exposed },
    { label: "Performing", count: tBuckets.Performing, color: TIER_COLOR.Performing },
    { label: "Elite", count: tBuckets.Elite, color: TIER_COLOR.Elite },
  ];
  donutLegend.forEach((d, i) => {
    const ly = 222 + i * 7;
    fill(doc, d.color);
    doc.circle(95, ly - 1, 1.6, "F");
    ink(doc, TEXT_DARK);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`${d.label}`, 100, ly);
    ink(doc, TEXT_MUTED);
    doc.setFont("helvetica", "bold");
    doc.text(String(d.count), 130, ly, { align: "right" });
  });

  // Right-hand caption
  ink(doc, NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Category mix at a glance", 145, 222);
  ink(doc, TEXT_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const cap = doc.splitTextToSize(
    "How many of your wellness categories fall into each tier. Use this to spot where you compound and where you leak.",
    50
  );
  doc.text(cap, 145, 230);

  centralFooter(doc, 1, 3);

  // ============ PAGE 2 — 14-Category Bar Infographic ============
  doc.addPage();
  pageBg(doc);
  navyTopRule(doc);
  brandBar(doc);

  ink(doc, TEXT_DARK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("Category Breakdown", 20, 36);

  ink(doc, TEXT_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("How you scored across each pillar of wellness, ranked highest first.", 20, 43);

  // Axis ticks 0/25/50/75/100
  const trackX = 60;
  const trackWidth = 130;
  ink(doc, TEXT_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  [0, 25, 50, 75, 100].forEach((t) => {
    const tx = trackX + (trackWidth * t) / 100;
    doc.text(`${t}`, tx, 52, { align: "center" });
    stroke(doc, TRACK);
    doc.setLineWidth(0.2);
    doc.line(tx, 54, tx, 250);
  });

  let cy = 60;
  const rowH = 13;
  cats.forEach((c) => {
    if (cy > 246) return;

    // Category label (left)
    ink(doc, TEXT_DARK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(c.category, 20, cy + 2);

    // Track
    fill(doc, TRACK);
    doc.roundedRect(trackX, cy - 1, trackWidth, 5, 1.5, 1.5, "F");

    // Filled bar
    const barColor =
      c.pct <= 25 ? TIER_COLOR.Critical :
      c.pct <= 50 ? TIER_COLOR.Exposed :
      c.pct <= 75 ? TIER_COLOR.Performing :
      TIER_COLOR.Elite;
    fill(doc, barColor);
    const w = Math.max(2, (trackWidth * c.pct) / 100);
    doc.roundedRect(trackX, cy - 1, w, 5, 1.5, 1.5, "F");

    // Value (right of track)
    ink(doc, NAVY);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(`${c.pct}%`, 195, cy + 2.5, { align: "right" });

    cy += rowH;
  });

  // Tier legend strip at bottom
  const legendY = 258;
  ink(doc, NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setCharSpace(1.5);
  doc.text("TIER KEY", 20, legendY);
  doc.setCharSpace(0);
  const stripLegend = [
    { label: "Critical 0–25", color: TIER_COLOR.Critical },
    { label: "Exposed 26–50", color: TIER_COLOR.Exposed },
    { label: "Performing 51–75", color: TIER_COLOR.Performing },
    { label: "Elite 76–100", color: TIER_COLOR.Elite },
  ];
  let lx = 45;
  stripLegend.forEach((s) => {
    fill(doc, s.color);
    doc.roundedRect(lx, legendY - 3, 4, 4, 1, 1, "F");
    ink(doc, TEXT_DARK);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(s.label, lx + 6, legendY);
    lx += doc.getTextWidth(s.label) + 16;
  });

  centralFooter(doc, 2, 3);

  // ============ PAGE 3 — Numbered 30-Day Action Cards + Disclaimer ============
  doc.addPage();
  pageBg(doc);
  navyTopRule(doc);
  brandBar(doc);

  ink(doc, TEXT_DARK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("Your Next 30 Days", 20, 36);

  ink(doc, TEXT_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Tier-specific actions for the ${tier} band. Pick one this week, layer the rest in across 30 days.`, 20, 43);

  const guidance = TIER_GUIDANCE[tier] || TIER_GUIDANCE.Performing;
  let gyc = 58;
  guidance.forEach((g, i) => {
    const cardH = 36;
    // card background
    fill(doc, PANEL_TINT);
    doc.roundedRect(20, gyc, 170, cardH, 3, 3, "F");

    // numbered badge
    fill(doc, tierColor);
    doc.circle(33, gyc + cardH / 2, 7, "F");
    ink(doc, "#FFFFFF");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(String(i + 1), 33, gyc + cardH / 2 + 2, { align: "center" });

    // heading
    ink(doc, NAVY);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(`Action ${i + 1}`, 46, gyc + 11);

    // body
    ink(doc, TEXT_DARK);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    const lines = doc.splitTextToSize(g, 138);
    doc.text(lines, 46, gyc + 18);

    gyc += cardH + 5;
  });

  // Medical disclaimer block — always present
  fill(doc, NAVY_DEEP);
  doc.roundedRect(20, 218, 170, 50, 3, 3, "F");
  ink(doc, "#FFFFFF");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setCharSpace(1.5);
  doc.text("MEDICAL DISCLAIMER", 26, 228);
  doc.setCharSpace(0);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const disc = doc.splitTextToSize(
    "This report is for educational and informational purposes only and does not constitute medical advice, diagnosis or treatment. Always consult a qualified healthcare professional regarding your individual circumstances. Fitest, Leadsopedia Limited and the issuing licence holder accept no liability for outcomes arising from reliance on these results.",
    158
  );
  doc.text(disc, 26, 236);

  centralFooter(doc, 3, 3);

  doc.save(`fitest-audit-${(name || "report").replace(/\s+/g, "-").toLowerCase()}.pdf`);
}
